import React from 'react';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { ToolbarButton } from './ToolbarButton';
import { ActionMenu } from './ActionMenu';
import { PanelActions } from '../layout/Panel';

export interface DownloadFormat {
  key: string;
  /** "Excel (.xlsx)", "CSV", "Image (.png)". */
  label: string;
  /** A word on what's in the file, e.g. "All 1,248 rows". */
  description?: string;
  onDownload: () => void;
}

/**
 * Whether downloads are offered here at all. A page that must not hand out
 * files (a board opened from a shared link that does not allow downloads)
 * wraps itself in `<DownloadsAllowed value={false}>`, and every DownloadButton
 * inside draws nothing. On by default.
 */
const DownloadsAllowedContext = React.createContext(true);

export function DownloadsAllowed({ value, children }: { value: boolean; children: React.ReactNode }) {
  return <DownloadsAllowedContext.Provider value={value}>{children}</DownloadsAllowedContext.Provider>;
}

/** Whether downloads are offered here: for anything else that hands out a file. */
export function useDownloadsAllowed(): boolean {
  return React.useContext(DownloadsAllowedContext);
}

export interface DownloadButtonProps {
  formats: DownloadFormat[];
  /** Said on hover of a single-format button, e.g. "Download this table as Excel, as shown". */
  tooltip?: string;
  /** A file is being prepared. */
  loading?: boolean;
  disabled?: boolean;
  /**
   * Just the icon, named on hover. Left out, it follows the panel it sits in:
   * the word in a roomy panel, the icon in a narrow one. Set it only where the
   * caller measures its own room, like a chat result card.
   */
  compact?: boolean;
}

/**
 * The one way to download anything — a table, a chart — so it reads the same
 * everywhere: the same word, icon and place in a header.
 *
 * With one format it is a plain button that downloads straight away; with
 * several, "Download ▾" opens the choice. In a narrow panel it keeps the
 * behaviour and drops the word, as Expand does beside it; anywhere else the
 * word stays.
 */
export function DownloadButton({ formats, tooltip, loading, disabled, compact: asked }: DownloadButtonProps) {
  const allowed = useDownloadsAllowed();
  const narrow = !React.useContext(PanelActions).roomy;
  const compact = asked ?? narrow;
  if (!allowed || formats.length === 0) return null;
  const icon = <FileDownloadOutlined fontSize="small" />;
  if (formats.length === 1) {
    const [only] = formats;
    return (
      <ToolbarButton
        icon={icon}
        onClick={only.onDownload}
        tooltip={tooltip ?? `Download as ${only.label}`}
        aria-label="Download"
        loading={loading}
        loadingLabel="Preparing"
        disabled={disabled}
      >
        {compact ? undefined : 'Download'}
      </ToolbarButton>
    );
  }
  return (
    <ActionMenu
      label={compact ? undefined : 'Download'}
      aria-label="Download"
      icon={icon}
      disabled={disabled || loading}
      items={formats.map((f) => ({ key: f.key, label: f.label, description: f.description, onSelect: f.onDownload }))}
    />
  );
}
